// ---------------------------------------------------------------------------
// DSS-19: Scroll effects types, constants, and data attribute generator
// ---------------------------------------------------------------------------

import type {
  DssBlurEffect as BlurEffect,
  DssFadeDirection as FadeDirection,
  DssHorizontalScrollEffect as HorizontalScrollEffect,
  DssRotateDirection as RotateDirection,
  DssRotateEffect as RotateEffect,
  DssScaleDirection as ScaleDirection,
  DssScaleEffect as ScaleEffect,
  DssScrollDirection as ScrollDirection,
  DssScrollEffects as ScrollEffectsConfig,
  DssTransparencyEffect as TransparencyEffect,
  DssVerticalScrollEffect as VerticalScrollEffect,
} from "@ease/page-builder";

export type {
  BlurEffect,
  FadeDirection,
  HorizontalScrollEffect,
  RotateDirection,
  RotateEffect,
  ScaleDirection,
  ScaleEffect,
  ScrollDirection,
  ScrollEffectsConfig,
  TransparencyEffect,
  VerticalScrollEffect,
};

// ── Constants ──
export const MIN_SPEED = 0;
export const MAX_SPEED = 10;
export const MIN_VIEWPORT = 0;
export const MAX_VIEWPORT = 100;
export const MIN_BLUR = 0;
export const MAX_BLUR = 20;
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 3;

export const DEFAULT_SCROLL_CONFIG: ScrollEffectsConfig = {
  vertical: { enabled: false, speed: 5, direction: "up" },
  horizontal: { enabled: false, speed: 5 },
  transparency: { enabled: false, direction: "in", viewportStart: 0, viewportEnd: 100 },
  blur: { enabled: false, maxBlur: 10, viewportStart: 0, viewportEnd: 100 },
  rotate: { enabled: false, direction: "cw", speed: 5, viewportStart: 0, viewportEnd: 100 },
  scale: { enabled: false, direction: "grow", rangeMin: 0.5, rangeMax: 1.5, viewportStart: 0, viewportEnd: 100 },
};

// ── NaN guard ──
export function safeNum(val: number, min: number, max: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(n, min), max);
}

// ── Data attribute generator ──
export function generateScrollDataAttributes(config: ScrollEffectsConfig): Record<string, string> {
  const attrs: Record<string, string> = {};

  if (config.vertical.enabled) {
    attrs["data-scroll-vertical-speed"] = String(safeNum(config.vertical.speed, MIN_SPEED, MAX_SPEED));
    attrs["data-scroll-vertical-direction"] = config.vertical.direction;
  }
  if (config.horizontal.enabled) {
    attrs["data-scroll-horizontal-speed"] = String(safeNum(config.horizontal.speed, MIN_SPEED, MAX_SPEED));
  }
  if (config.transparency.enabled) {
    attrs["data-scroll-transparency-direction"] = config.transparency.direction;
    attrs["data-scroll-transparency-start"] = String(safeNum(config.transparency.viewportStart, MIN_VIEWPORT, MAX_VIEWPORT));
    attrs["data-scroll-transparency-end"] = String(safeNum(config.transparency.viewportEnd, MIN_VIEWPORT, MAX_VIEWPORT));
  }
  if (config.blur.enabled) {
    attrs["data-scroll-blur-max"] = String(safeNum(config.blur.maxBlur, MIN_BLUR, MAX_BLUR));
    attrs["data-scroll-blur-start"] = String(safeNum(config.blur.viewportStart, MIN_VIEWPORT, MAX_VIEWPORT));
    attrs["data-scroll-blur-end"] = String(safeNum(config.blur.viewportEnd, MIN_VIEWPORT, MAX_VIEWPORT));
  }
  if (config.rotate.enabled) {
    attrs["data-scroll-rotate-direction"] = config.rotate.direction;
    attrs["data-scroll-rotate-speed"] = String(safeNum(config.rotate.speed, MIN_SPEED, MAX_SPEED));
    attrs["data-scroll-rotate-start"] = String(safeNum(config.rotate.viewportStart, MIN_VIEWPORT, MAX_VIEWPORT));
    attrs["data-scroll-rotate-end"] = String(safeNum(config.rotate.viewportEnd, MIN_VIEWPORT, MAX_VIEWPORT));
  }
  if (config.scale.enabled) {
    attrs["data-scroll-scale-direction"] = config.scale.direction;
    attrs["data-scroll-scale-min"] = String(safeNum(config.scale.rangeMin, MIN_SCALE, MAX_SCALE));
    attrs["data-scroll-scale-max"] = String(safeNum(config.scale.rangeMax, MIN_SCALE, MAX_SCALE));
    attrs["data-scroll-scale-start"] = String(safeNum(config.scale.viewportStart, MIN_VIEWPORT, MAX_VIEWPORT));
    attrs["data-scroll-scale-end"] = String(safeNum(config.scale.viewportEnd, MIN_VIEWPORT, MAX_VIEWPORT));
  }

  return attrs;
}
